import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import Checkbox from "./Checkbox";
import Icon from "./Icon";
import useWebsocket from '../hooks/websocketHook';
import { useOfflineProcessing } from "./OfflineProcessor";
import useAppState, { useNetworkState } from "../hooks/appStateHook";
import useClientConfig from "../hooks/clientConfigHook";
import { usePendingUpdates } from "../reducers/pendingUpdateSlice";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import { buildColor } from "../utils/tableUtilities";
import { useDispatch } from "react-redux";

export const NetworkStatusIndicator = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const clientConfig = useClientConfig();
    const pendingUpdates = usePendingUpdates();
    const { token } = useAppState();
    const { isOnline, wsStatus, autoconnect, setAutoconnect } = useNetworkState();
    const { disconnect } = useWebsocket();
    const { count, autoprocess, isProcessing, startProcessing, cancelProcessing, toggleAutoprocess } = useOfflineProcessing();
    const [showDialog, setShowDialog] = useState(false);

    const { color, label } = useMemo(() => {
        if (!isOnline) {
            return { color: clientConfig.enableOfflineMode ? '#95a5a6' : '#c0392b', label: t('offline') };
        } else if (!token) {
            return { color: '#2ecc71', label: t('online') };
        } else {
            switch (wsStatus) {
                case 'closed':
                    return { color: clientConfig.enableOfflineMode ? '#95a5a6' : '#c0392b', label: t('offline') };
                case 'connecting':
                    return { color: '#f1c40f', label: t('connecting') };
                case 'authenticated':
                    return { color: '#2ecc71', label: t('online') };
                default:
                    throw new Error('Unknown websocket status ' + wsStatus);
            }
        }
    }, [t, isOnline, wsStatus, token, clientConfig.enableOfflineMode]);

    const style = useMemo(() => buildColor(color), [color]);

    const setOnline = useCallback(value => {
        if (value) {
            setAutoconnect(dispatch, 1);
        } else {
            disconnect()
            setAutoconnect(dispatch, 0);
        }
    }, [disconnect, setAutoconnect, dispatch])

    return <>
        <ReactNodeDialog showModal={showDialog} title={t('offlineSettings')} onClose={() => setShowDialog(false)} footerControls={<>
            {isProcessing && count > 0 && <Button variant="danger" onClick={() => cancelProcessing()}><Icon variant="solid" kind="stop"></Icon> {t('processStop')}</Button>}
            {!isProcessing && count > 0 && isOnline && wsStatus === 'authenticated' && <Button variant="success" onClick={() => startProcessing()}><Icon variant="solid" kind="play"></Icon> {t('processPending')}</Button>}
        </>}>
            <Checkbox type="switch" checked={autoprocess} placeholder={t('autoprocessToggleLabel')} onChange={() => toggleAutoprocess()}></Checkbox>
            <Checkbox type="switch" checked={autoconnect === -1} placeholder="Autoconnect" onChange={value => setAutoconnect(dispatch, value ? -1 : 0)}></Checkbox>
            {autoconnect === 0 && <Checkbox type="switch" checked={wsStatus === 'authenticated'} disabled={!isOnline} placeholder="Online" onChange={setOnline}></Checkbox>}
        </ReactNodeDialog>
        <div className="h-100 d-flex flex-column px-2" style={{ ...style, justifyContent: 'center', alignItems: 'center', fontSize: '9pt' }} onClick={e => { e.preventDefault(); e.stopPropagation(); setShowDialog(true); }}>
            <div className="d-flex flex-row" style={{ flex: 1, textAlign: 'center', minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
                {label}
            </div>
            {clientConfig.enableOfflineMode && (!isOnline || wsStatus !== 'authenticated') && pendingUpdates.length > 0 && <div className="pb-2" style={{ fontSize: '8pt', textAlign: 'center' }}>
                {pendingUpdates.length} Pending
            </div>}
            {clientConfig.enableOfflineMode && (isOnline && wsStatus === 'authenticated') && count > 0 && <div className="pb-2" style={{ fontSize: '8pt', textAlign: 'center' }}>
                {isProcessing && <Icon variant="solid" kind="rotate"></Icon>} {count} Pending
            </div>}
        </div>
    </>;
};
